// @ts-check
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import adminAuthService from '../services/adminAuthService';
import adminWhatsAppService from '../services/adminWhatsAppService';
import { useToast } from './ToastContext';

/**
 * Admin-wide WhatsApp "new message" alerts. Pure logic: polls the unread summary, exposes the
 * counters and fires the alerts (beep, toast, desktop notification, tab title). Rendering the nav
 * badge lives in AdminLayout; the inbox talks to this through `useWhatsAppUnread`.
 */

export const POLL_VISIBLE_MS = 20_000;
export const POLL_HIDDEN_MS = 20_000; // same pace as visible: the bell is for when you're on another tab
export const POLL_MAX_BACKOFF_MS = 5 * 60_000;
export const MUTE_STORAGE_KEY = 'kaay_whatsapp_alert_muted';

const NOOP_VALUE = Object.freeze({
  unreadTotal: 0,
  latest: null,
  setActivePhone: () => {},
  refresh: () => Promise.resolve(),
  muted: false,
  setMuted: () => {},
  desktopPermission: 'unsupported',
  requestDesktopPermission: () => Promise.resolve('unsupported'),
});

const WhatsAppUnreadContext = createContext(/** @type {any} */ (null));

/** Safe outside a provider (tests, other pages): everything is a no-op. */
export const useWhatsAppUnread = () => useContext(WhatsAppUnreadContext) || NOOP_VALUE;

const TITLE_PREFIX = /^\(\d+\+?\)\s/;
const FLASH_PREFIX = '\u{1F4AC} New message | ';
const FLASH_RE = /^\u{1F4AC} New message \| /u;

const readMuted = () => {
  try {
    return localStorage.getItem(MUTE_STORAGE_KEY) === '1';
  } catch {
    return false;
  }
};

const currentPermission = () => (typeof Notification === 'undefined' ? 'unsupported' : Notification.permission);

let sharedCtx = /** @type {any} */ (null);
/** One lazily created AudioContext for the whole app; never one per beep. */
const getAudioContext = () => {
  try {
    if (sharedCtx) return sharedCtx;
    const Ctx = window.AudioContext || /** @type {any} */ (window).webkitAudioContext;
    if (!Ctx) return null;
    sharedCtx = new Ctx();
    return sharedCtx;
  } catch {
    return null;
  }
};

/** Drops the cached context so a test can install a fresh fake. */
export const resetAudioContext = () => {
  sharedCtx = null;
};

/** Browsers keep audio suspended until a user gesture; call this from one. */
export const unlockAudio = () => {
  try {
    const ctx = getAudioContext();
    if (ctx && ctx.state !== 'running') ctx.resume?.();
  } catch {
    /* ignore */
  }
};

/** A two-note "ding-dong" bell with a long decay on the shared context; silently does nothing if audio is unavailable or still blocked. */
export const playBeep = async () => {
  try {
    const ctx = getAudioContext();
    if (!ctx) return;
    if (ctx.state !== 'running') await ctx.resume?.();
    if (ctx.state !== 'running') return;
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = 'sine';
    osc.frequency.setValueAtTime(1318.5, ctx.currentTime);
    osc.frequency.setValueAtTime(987.8, ctx.currentTime + 0.22);
    gain.gain.setValueAtTime(0.0001, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.3, ctx.currentTime + 0.015);
    gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 1.1);
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.start();
    osc.stop(ctx.currentTime + 1.15);
  } catch {
    /* audio is a nicety; never break polling */
  }
};

let sleepWorker = /** @type {Worker | null | undefined} */ (undefined);
const sleepWaiters = new Map();
let sleepSeq = 0;
/**
 * A timer that keeps its pace in a background browser tab. Plain setTimeout is throttled to about once a
 * minute in hidden tabs, which would delay the bell; a Web Worker's timers are not throttled that way.
 * Falls back to setTimeout where workers are unavailable (tests, strict CSP). Returns a cancel function.
 */
export const backgroundSleep = (/** @type {number} */ ms, /** @type {() => void} */ fn) => {
  if (sleepWorker === undefined) {
    try {
      if (typeof Worker === 'undefined' || typeof Blob === 'undefined' || !URL?.createObjectURL)
        throw new Error('no worker');
      const src = 'onmessage=e=>{const{id,ms}=e.data;setTimeout(()=>postMessage(id),ms)}';
      sleepWorker = new Worker(URL.createObjectURL(new Blob([src], { type: 'text/javascript' })));
      sleepWorker.onmessage = (e) => {
        const cb = sleepWaiters.get(e.data);
        sleepWaiters.delete(e.data);
        cb?.();
      };
      sleepWorker.onerror = () => {
        sleepWorker = null;
      };
    } catch {
      sleepWorker = null;
    }
  }
  if (!sleepWorker) {
    const t = setTimeout(fn, ms);
    return () => clearTimeout(t);
  }
  const id = ++sleepSeq;
  sleepWaiters.set(id, fn);
  sleepWorker.postMessage({ id, ms });
  return () => sleepWaiters.delete(id);
};

const CHANNEL = 'kaay-whatsapp-alerts';
const LOCK_HOLD_MS = 60_000;
const REMOTE_FRESH_MS = 30_000;
const HEARTBEAT_MS = 10_000;

/**
 * Cross-tab dedupe: resolves true when THIS tab should alert for `key`. Uses the Web Locks API, holding
 * the lock for a minute so other tabs' later polls lose. Where unsupported, every tab alerts.
 */
const claimAlert = (/** @type {string} */ key) => {
  const locks = typeof navigator !== 'undefined' ? /** @type {any} */ (navigator).locks : null;
  if (!locks?.request) return Promise.resolve(true);
  return new Promise((resolve) => {
    try {
      locks
        .request(`kaay-wa-alert:${key}`, { ifAvailable: true }, (/** @type {any} */ lock) => {
          resolve(!!lock);
          return lock ? new Promise((r) => setTimeout(r, LOCK_HOLD_MS)) : undefined;
        })
        .catch(() => resolve(true));
    } catch {
      resolve(true);
    }
  });
};

const truncate = (/** @type {string} */ text, max = 120) => (text.length > max ? `${text.slice(0, max)}...` : text);

const messageTime = (/** @type {any} */ latest) => {
  const t = latest?.at ? new Date(latest.at).getTime() : NaN;
  return Number.isNaN(t) ? null : t;
};

/**
 * @param {{children: any, onOpenChat?: (phone: string) => void}} props
 * `onOpenChat` is called when the toast or desktop notification is clicked.
 */
export const WhatsAppUnreadProvider = ({ children, onOpenChat }) => {
  const toast = useToast();
  const [unreadTotal, setUnreadTotal] = useState(0);
  const [latest, setLatest] = useState(/** @type {any} */ (null));
  const [muted, setMutedState] = useState(readMuted);
  const [desktopPermission, setDesktopPermission] = useState(currentPermission);
  // While this tab is in the background and a message lands, the tab title flashes until you come back.
  const [flashing, setFlashing] = useState(false);
  const [flashOn, setFlashOn] = useState(false);

  // Alerts key off the newest unread message (latest.at, else id), NOT the count: a mark-read landing in
  // the same interval as a new arrival would otherwise cancel it out. The count only drives badge/title.
  const seenAt = useRef(/** @type {number | null} */ (null));
  const seenKey = useRef(/** @type {string | null} */ (null));
  const seeded = useRef(false);
  const forbidden = useRef(false);
  const remoteActive = useRef(/** @type {{phone: string, ts: number} | null} */ (null));
  const activePhone = useRef(/** @type {string | null} */ (null));
  const mutedRef = useRef(muted);
  const toastRef = useRef(toast);
  const openChatRef = useRef(onOpenChat);
  const failures = useRef(0);
  const inFlight = useRef(/** @type {Promise<void> | null} */ (null));
  const fetchRef = useRef(/** @type {() => Promise<void>} */ (() => Promise.resolve()));
  useEffect(() => {
    mutedRef.current = muted;
    toastRef.current = toast;
    openChatRef.current = onOpenChat;
  });

  // Set by the polling effect once the cross-tab channel exists; lets a chat/focus change be announced at once.
  const announceRef = useRef(/** @type {() => void} */ (() => {}));
  const setActivePhone = useCallback((/** @type {string | null} */ phone) => {
    activePhone.current = phone || null;
    announceRef.current();
  }, []);

  const setMuted = useCallback((/** @type {boolean} */ value) => {
    unlockAudio();
    setMutedState(value);
    try {
      localStorage.setItem(MUTE_STORAGE_KEY, value ? '1' : '0');
    } catch {
      /* storage blocked: the choice just won't persist */
    }
  }, []);

  const requestDesktopPermission = useCallback(async () => {
    unlockAudio();
    if (typeof Notification === 'undefined') return 'unsupported';
    try {
      const result = await Notification.requestPermission();
      setDesktopPermission(result);
      return result;
    } catch {
      return currentPermission();
    }
  }, []);

  const alertFor = useCallback(async (/** @type {any} */ info) => {
    if (!adminAuthService.isAuthenticated()) return;
    const focused = !document.hidden && (typeof document.hasFocus === 'function' ? document.hasFocus() : true);
    // Suppression looks at the newest message only: if the open chat's message is the newest in this
    // interval, an older one from another chat in the same interval is not announced (accepted trade-off).
    if (focused && info?.phone && info.phone === activePhone.current) return;
    const remote = remoteActive.current;
    if (remote && info?.phone === remote.phone && Date.now() - remote.ts < REMOTE_FRESH_MS) return; // another tab is showing that chat
    const key = String(info?.id ?? info?.at ?? Date.now());
    if (!(await claimAlert(key))) return; // another tab already alerted for this message
    const who = info?.name || info?.phone || 'a customer';
    const preview = info?.preview ? truncate(String(info.preview)) : '';
    if (!mutedRef.current) playBeep();
    if (document.hidden) setFlashing(true);
    toastRef.current.info(
      preview ? `New WhatsApp message from ${who}: ${preview}` : `New WhatsApp message from ${who}`,
      8000,
      info?.phone ? () => openChatRef.current?.(info.phone) : undefined,
    );
    try {
      if (typeof Notification !== 'undefined' && Notification.permission === 'granted') {
        // Name only: message text must not show on lock screens or shared screens.
        // `renotify` is a real option but missing from the TS DOM typings.
        const n = new Notification(
          `New WhatsApp message from ${who}`,
          /** @type {NotificationOptions} */ (
            /** @type {any} */ ({ tag: 'kaay-whatsapp', renotify: true, silent: false })
          ),
        );
        n.onclick = () => {
          window.focus?.();
          if (info?.phone) openChatRef.current?.(info.phone);
          n.close?.();
        };
      }
    } catch {
      /* desktop notifications are best effort */
    }
  }, []);

  const fetchSummary = useCallback(() => {
    if (forbidden.current || !adminAuthService.isAuthenticated()) return Promise.resolve();
    if (inFlight.current) return inFlight.current;
    const run = (async () => {
      try {
        const summary = await adminWhatsAppService.getUnreadSummary();
        failures.current = 0;
        if (!summary) return;
        const total = Number(summary.unread_total) || 0;
        const info = summary.latest || null;
        setUnreadTotal(total);
        setLatest(info);
        const at = messageTime(info);
        const key = info ? String(info.id ?? info.at ?? '') : null;
        const isNew = info && (at !== null && seenAt.current !== null ? at > seenAt.current : key !== seenKey.current);
        if (seeded.current && isNew) alertFor(info);
        seeded.current = true; // the first load only seeds
        if (at !== null) seenAt.current = Math.max(seenAt.current ?? 0, at);
        if (key !== null) seenKey.current = key;
      } catch (err) {
        if (/** @type {any} */ (err)?.status === 403)
          forbidden.current = true; // no WhatsApp permission: stop polling
        else failures.current += 1;
      } finally {
        inFlight.current = null;
      }
    })();
    inFlight.current = run;
    return run;
  }, [alertFor]);
  fetchRef.current = fetchSummary;

  // A refresh (after mark-read) must not reuse a request issued before the change committed.
  const refresh = useCallback(() => {
    const pending = inFlight.current;
    return pending ? pending.then(() => fetchRef.current()) : fetchRef.current();
  }, []);

  // Poll loop: 20 s while visible, 60 s hidden, exponential backoff after failures, idle when logged out.
  useEffect(() => {
    let cancelTimer = () => {};
    let stopped = false;
    const delay = () => {
      const base = document.hidden ? POLL_HIDDEN_MS : POLL_VISIBLE_MS;
      return Math.min(base * 2 ** failures.current, POLL_MAX_BACKOFF_MS);
    };
    const schedule = () => {
      if (stopped || forbidden.current) return;
      cancelTimer();
      cancelTimer = backgroundSleep(delay(), async () => {
        await fetchRef.current();
        schedule();
      });
    };
    const onVisibility = () => {
      setDesktopPermission(currentPermission());
      if (!document.hidden) fetchRef.current();
      schedule();
    };
    const onGesture = () => unlockAudio();
    document.addEventListener('pointerdown', onGesture, { once: true });
    document.addEventListener('keydown', onGesture, { once: true });

    // Tell other tabs which chat this (focused) tab is showing, so they stay quiet about it.
    let channel = /** @type {any} */ (null);
    let beat = null;
    let removeAnnounce = () => {};
    try {
      if (typeof BroadcastChannel !== 'undefined') {
        channel = new BroadcastChannel(CHANNEL);
        channel.onmessage = (/** @type {any} */ e) => {
          const d = e.data;
          remoteActive.current = d && d.phone ? { phone: d.phone, ts: Date.now() } : null;
        };
        const announce = () => {
          const focused = !document.hidden && document.hasFocus?.();
          channel.postMessage({ phone: focused ? activePhone.current : null });
        };
        announceRef.current = announce;
        beat = setInterval(announce, HEARTBEAT_MS);
        // Tell other tabs the moment this one gains/loses focus, not up to a heartbeat later.
        window.addEventListener('focus', announce);
        window.addEventListener('blur', announce);
        document.addEventListener('visibilitychange', announce);
        removeAnnounce = () => {
          window.removeEventListener('focus', announce);
          window.removeEventListener('blur', announce);
          document.removeEventListener('visibilitychange', announce);
        };
        announce();
      }
    } catch {
      channel = null;
    }
    fetchRef.current().then(schedule);
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      stopped = true;
      cancelTimer();
      document.removeEventListener('visibilitychange', onVisibility);
      document.removeEventListener('pointerdown', onGesture);
      document.removeEventListener('keydown', onGesture);
      if (beat) clearInterval(beat);
      removeAnnounce();
      announceRef.current = () => {};
      channel?.close?.();
    };
  }, []);

  // Flash the tab title while a background tab has a fresh message; stop as soon as the admin returns.
  useEffect(() => {
    if (!flashing) return undefined;
    const stop = () => {
      if (!document.hidden) setFlashing(false);
    };
    const blink = setInterval(() => setFlashOn((v) => !v), 1000);
    document.addEventListener('visibilitychange', stop);
    window.addEventListener('focus', stop);
    return () => {
      clearInterval(blink);
      document.removeEventListener('visibilitychange', stop);
      window.removeEventListener('focus', stop);
      setFlashOn(false);
    };
  }, [flashing]);

  // Tab title: unread count prefix, plus the flashing "New message" cue while a background tab is alerting.
  useEffect(() => {
    const base = document.title.replace(FLASH_RE, '').replace(TITLE_PREFIX, '');
    let title = unreadTotal > 0 ? `(${unreadTotal > 99 ? '99+' : unreadTotal}) ${base}` : base;
    if (flashing && flashOn) title = FLASH_PREFIX + title;
    document.title = title;
    return () => {
      document.title = document.title.replace(FLASH_RE, '').replace(TITLE_PREFIX, '');
    };
  }, [unreadTotal, flashing, flashOn]);

  const value = useMemo(
    () => ({
      unreadTotal,
      latest,
      setActivePhone,
      refresh,
      muted,
      setMuted,
      desktopPermission,
      requestDesktopPermission,
    }),
    [unreadTotal, latest, setActivePhone, refresh, muted, setMuted, desktopPermission, requestDesktopPermission],
  );

  return <WhatsAppUnreadContext.Provider value={value}>{children}</WhatsAppUnreadContext.Provider>;
};

export default WhatsAppUnreadContext;
